import { Trans, useLingui } from '@lingui/react/macro';
import { min } from 'date-fns';

import { EmptyState, IllustrationNoResults } from '@/components/EmptyState';
import { TableQueryWrapper } from '@/components/TableQueryWrapper/TableQueryWrapper';
import { PaginationFooter } from '@/design_system/Pagination/Pagination';
import Stack from '@/design_system/Stack';
import { Body, Cell, Column, Header, Row, Table } from '@/design_system/Table/Table';
import Paragraph from '@/design_system/Typography/Paragraph';
import { InvitationFromGetMany } from '@/models/userRole';
import { formatDate } from '@/utils/date';

import { RemoveUser } from './components/RemoveUser';
import { ResendInvitation } from './components/ResendInvitation';
import { UpdateUserRoles } from './components/UpdateUserRoles';

export const INVITATIONS_PER_PAGE = 10;

interface InvitationsTableProps {
  invitations: InvitationFromGetMany[];
  isLoading: boolean;
  isError: boolean;
  page: number;
  onPageChange: (page: number) => void;
  count: number | undefined;
}

export const InvitationsTable = ({
  invitations,
  isLoading,
  isError,
  page,
  onPageChange,
  count,
}: InvitationsTableProps) => {
  const { t } = useLingui();

  return (
    <TableQueryWrapper isLoading={isLoading} isError={isError}>
      {invitations.length === 0 ? (
        <EmptyState
          subtitle={
            <Stack>
              <Paragraph>
                <Trans id="settings.organization.members.invitations.no-results.1">
                  No results are matching your criteria.
                </Trans>
              </Paragraph>
              <Paragraph>
                <Trans id="settings.organization.members.invitations.no-results.2">
                  Please try with different keywords or filters.
                </Trans>
              </Paragraph>
            </Stack>
          }
          illustration={<IllustrationNoResults />}
        />
      ) : (
        <Table
          ariaLabel={t({
            id: 'settings.organization.members.invitations.table.label',
            message: 'Invitations',
          })}
          columnWidths={['1fr', '1fr', '200px', '120px']}
          bordered
        >
          <Header>
            <Row>
              <Column>
                <Trans id="settings.organization.members.invitations.table.header.email">
                  Email
                </Trans>
              </Column>
              <Column>
                <Trans id="settings.organization.members.invitations.table.header.roles">
                  Roles
                </Trans>
              </Column>
              <Column>
                <Trans id="settings.organization.members.invitations.table.header.invitation-date">
                  Invitation date
                </Trans>
              </Column>
              <Column
                ariaLabel={t({
                  id: 'settings.users.column.actions',
                  message: 'Actions',
                })}
              />
            </Row>
          </Header>
          <Body>
            {invitations.map((invitation) => (
              <InvitationRow key={invitation.email} invitation={invitation} />
            ))}
          </Body>
          <PaginationFooter
            page={page}
            itemsPerPage={INVITATIONS_PER_PAGE}
            count={count}
            onPageChange={onPageChange}
          />
        </Table>
      )}
    </TableQueryWrapper>
  );
};

const InvitationRow = ({ invitation }: { invitation: InvitationFromGetMany }) => {
  const rolesGrouped = Object.values(
    invitation.userRoles.reduce(
      (acc, userRole) => {
        if (!acc[userRole.role.id]) {
          acc[userRole.role.id] = {
            role: userRole.role,
            entities: [],
          };
        }
        if (userRole.entity) {
          acc[userRole.role.id].entities.push(userRole.entity);
        }

        return acc;
      },
      {} as Record<
        string,
        {
          role: {
            id: string;
            name: string;
          };
          entities: {
            id: string;
            name: string;
          }[];
        }
      >
    )
  );

  return (
    <Row key={invitation.email}>
      <Cell className="sentry-mask">
        <Paragraph size="100">{invitation.email}</Paragraph>
      </Cell>
      <Cell>
        <Stack gap="0.25rem">
          {rolesGrouped.map(({ role, entities }) => (
            <Paragraph key={role.id} size="100">
              {entities.length > 0
                ? `${role.name} (${entities.map((entity) => entity.name).join(', ')})`
                : role.name}
            </Paragraph>
          ))}
        </Stack>
      </Cell>
      <Cell>
        <Paragraph size="100">
          {formatDate(min(invitation.userRoles.map((userRole) => new Date(userRole.invitedAt))), {
            dateStyle: 'medium',
            timeStyle: 'short',
          })}
        </Paragraph>
      </Cell>
      <Cell justifyContent="flex-end">
        <Stack row gap="4px">
          <ResendInvitation email={invitation.email} />
          <UpdateUserRoles
            user={{
              email: invitation.email,
              roles: invitation.userRoles,
            }}
          />
          <RemoveUser
            user={{
              email: invitation.email,
              roles: invitation.userRoles,
            }}
          />
        </Stack>
      </Cell>
    </Row>
  );
};
